import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

import { Eyebrow } from "~/components/ui/eyebrow";
import { cn } from "~/lib/utils";

interface SetupRowProps {
    label: string;
    value?: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    loading?: boolean;
    testId: string;
    showDivider?: boolean;
    interactive?: boolean;
}

export function SetupRow({
    label,
    value,
    onClick,
    disabled,
    loading,
    testId,
    showDivider = true,
    interactive = true,
}: SetupRowProps) {
    const content = (
        <>
            <span className="min-w-0 flex-1 sm:w-full sm:flex-none">
                <Eyebrow as="span" tone="muted" className="block">
                    {label}
                </Eyebrow>
                {loading ? (
                    <span className="mt-2 block h-5 w-2/3 animate-pulse rounded bg-stone-200 motion-reduce:animate-none" />
                ) : (
                    // label-md sizing is too small for the chosen value — this is the
                    // row's content, not its label. body-md at 500 keeps it in the ramp.
                    <span className="mt-1 flex flex-wrap items-center gap-2 font-body text-base font-medium leading-snug text-brand-forest-900">
                        {value}
                    </span>
                )}
            </span>
            {interactive ? (
                <>
                    <ChevronRight className="size-5 shrink-0 text-stone-400 sm:hidden" />
                    {/* AIDEV-NOTE: Withheld while disabled or loading — it reads as actionable
                        and is not. */}
                    {disabled || loading ? null : (
                        <span className="hidden font-body text-sm font-medium text-brand-forest-700 sm:mt-auto sm:inline-flex sm:items-center sm:gap-1">
                            Change
                            <ChevronRight className="size-4" />
                        </span>
                    )}
                </>
            ) : null}
        </>
    );
    const rowClassName = cn(
        "flex min-h-[78px] w-full items-center gap-4 px-5 py-4 text-left",
        interactive &&
            "transition-colors hover:bg-brand-forest-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-forest-500",
        interactive && "disabled:cursor-default disabled:hover:bg-transparent",
        // AIDEV-NOTE: The three rows become three columns from `sm` up (see
        // MeditationSetupOverlay), so the divider has to switch axis with them:
        // a bottom border below, a right border beside. The last row passes
        // showDivider={false} and gets neither.
        showDivider &&
            "border-b border-brand-forest-100 sm:border-b-0 sm:border-r",
        // Stack label above value in the column layout so a long meditation
        // title has the full column width instead of a squeezed half.
        "sm:min-h-[104px] sm:flex-col sm:items-start sm:justify-start sm:gap-2",
    );

    if (!interactive) {
        return (
            <div data-testid={testId} className={rowClassName}>
                {content}
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled || loading}
            data-testid={testId}
            className={rowClassName}
        >
            {content}
        </button>
    );
}
